import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  Login as LoginIcon,
  LockOutlined as LockIcon,
  Email as EmailIcon,
  Lock as PasswordIcon,
  Google as GoogleIcon,
  Apple as AppleIcon,
  Facebook as FacebookIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import NebenanHilfeLogo from '../UI/NebenanHilfeLogo';
import { signInWithGooglePopup } from '../../services/googleAuth';

const LoginForm = ({ onSwitchToRegister, onSwitchToReset, onRequireVerification, onRequireMFA }) => {
  const { login, socialLogin } = useAuth();
  const [formData, setFormData] = useState({
    contact: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.contact || !formData.password) {
      setError('Bitte fülle alle Felder aus');
      return;
    }

    setLoading(true);
    setError('');
    const result = await login(formData.contact, formData.password);
    setLoading(false);

    if (result.requireVerification && onRequireVerification) {
      onRequireVerification(result.email, result.devCode);
      return;
    }

    if (result.requireMFA && onRequireMFA) {
      onRequireMFA(result.email, result.devCode, result.mfaType);
      return;
    }

    if (!result.success) {
      setError(result.error);
    }
  };

  const [socialModal, setSocialModal] = useState({
    open: false,
    provider: 'Google',
    email: '',
    name: '',
    error: '',
  });

  const handleSocialClick = async (provider) => {
    setError('');

    // Echter 1-Click Google OAuth Dialog
    if (provider === 'Google') {
      try {
        setLoading(true);
        const profile = await signInWithGooglePopup();
        const result = await socialLogin(
          'Google',
          profile.socialId,
          profile.email,
          profile.firstName,
          profile.lastName,
          profile.avatar,
          true // isOAuthVerified = true (direkter Login, keine manuelle E-Mail nötig)
        );
        setLoading(false);
        if (result.requireVerification && onRequireVerification) {
          onRequireVerification(result.email, result.devCode);
          return;
        }
        if (result.requireMFA && onRequireMFA) {
          onRequireMFA(result.email, result.devCode, result.mfaType);
          return;
        }
        if (!result.success) {
          setError(result.error);
        }
        return;
      } catch (err) {
        setLoading(false);
        console.warn('Google Popup Error / Cancelled:', err);
        // Wenn der Nutzer das Popup nicht geschlossen hat, sondern ein Fehler aufgetreten ist:
        if (err.message && !err.message.includes('popup_closed') && !err.message.includes('abgebrochen')) {
          setError(err.message);
          return;
        }
        return;
      }
    }

    // Fallback für Apple / Facebook Dialog
    setSocialModal({
      open: true,
      provider,
      email: formData.contact.includes('@') ? formData.contact : '',
      name: '',
      error: '',
    });
  };

  const handleSocialSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!socialModal.email || !socialModal.email.includes('@')) {
      setSocialModal(prev => ({ ...prev, error: 'Bitte eine gültige E-Mail-Adresse angeben.' }));
      return;
    }

    let firstName = socialModal.provider;
    let lastName = 'Nutzer';

    if (socialModal.name && socialModal.name.trim()) {
      const parts = socialModal.name.trim().split(' ');
      firstName = parts[0];
      lastName = parts.slice(1).join(' ') || 'Nutzer';
    } else {
      const usernamePart = socialModal.email.split('@')[0];
      const parts = usernamePart.split(/[._-]/);
      if (parts.length >= 2) {
        firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
        lastName = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      } else if (parts.length === 1 && parts[0]) {
        firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
        lastName = 'Nutzer';
      }
    }

    setSocialModal(prev => ({ ...prev, submitting: true, error: '' }));

    const cleanEmail = socialModal.email.trim().toLowerCase();
    const socialId = `${socialModal.provider.toLowerCase()}-${cleanEmail}`;

    try {
      const result = await socialLogin(
        socialModal.provider,
        socialId,
        cleanEmail,
        firstName,
        lastName,
        '🌐',
        false
      );

      if (!result.success && !result.requireVerification && !result.requireMFA) {
        setSocialModal(prev => ({ ...prev, submitting: false, error: result.error || 'Anmeldung fehlgeschlagen.' }));
      } else if (result.requireVerification && onRequireVerification) {
        setSocialModal(prev => ({ ...prev, open: false, submitting: false }));
        onRequireVerification(result.email, result.devCode);
      } else if (result.requireMFA && onRequireMFA) {
        setSocialModal(prev => ({ ...prev, open: false, submitting: false }));
        onRequireMFA(result.email, result.devCode, result.mfaType);
      } else {
        setSocialModal(prev => ({ ...prev, open: false, submitting: false }));
      }
    } catch (err) {
      setSocialModal(prev => ({ ...prev, submitting: false, error: 'Verbindungsfehler bei der Anmeldung.' }));
    }
  };

  return (
    <Card sx={{ maxWidth: 440, mx: 'auto', borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: { xs: 4, md: 5 } }}>
        <Box textAlign="center" mb={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <NebenanHilfeLogo size="large" />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: 'text.primary', mt: 2, mb: 0.5 }}>
            Willkommen zurück!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Bitte melde dich an, um fortzufahren.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Social Media Login Options */}
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon sx={{ color: '#ea4335' }} />}
            onClick={() => handleSocialClick('Google')}
            disabled={loading}
            sx={{ py: 1.2, textTransform: 'none', fontWeight: 600, borderColor: '#e0e0e0', color: 'text.primary', borderRadius: 2 }}
          >
            Mit Google anmelden
          </Button>
        </Stack>

        <Divider sx={{ my: 2.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600 }}>
            ODER PER E-MAIL
          </Typography>
        </Divider>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            id="login-contact-input"
            fullWidth
            label="E-Mail oder Telefonnummer"
            name="contact"
            type="text"
            value={formData.contact}
            onChange={handleChange}
            margin="normal"
            placeholder="deine.email@beispiel.de oder Telefonnummer"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Passwort"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            placeholder="Dein Passwort"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PasswordIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Sichtbarkeit umschalten"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={!loading ? <LoginIcon /> : null}
            sx={{ 
              mt: 3, 
              mb: 3, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Anmelden'}
          </Button>

          <Box textAlign="center">
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={onSwitchToReset}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 0.5,
                mb: 1,
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'primary.main', textDecoration: 'none' }
              }}
            >
              <LockIcon fontSize="small" />
              Passwort vergessen?
            </Link>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2">
              Noch kein Konto?{' '}
              <Link
                component="button"
                type="button"
                onClick={onSwitchToRegister}
                sx={{ fontWeight: 700 }}
              >
                Jetzt kostenlos registrieren
              </Link>
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Social Media Login Modal */}
      <Dialog 
        open={socialModal.open} 
        onClose={() => setSocialModal(prev => ({ ...prev, open: false }))}
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 450, width: '100%' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          {socialModal.provider === 'Google' && <GoogleIcon sx={{ color: '#ea4335', fontSize: 28 }} />}
          {socialModal.provider === 'Apple' && <AppleIcon sx={{ fontSize: 28 }} />}
          {socialModal.provider === 'Facebook' && <FacebookIcon sx={{ color: '#1877f2', fontSize: 28 }} />}
          Mit {socialModal.provider} anmelden
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Gib deine {socialModal.provider}-Adresse ein, um dich direkt anzumelden oder dein Konto zu verknüpfen:
          </Typography>

          {socialModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {socialModal.error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSocialSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              autoFocus
              label={`${socialModal.provider}-Adresse`}
              type="email"
              placeholder={socialModal.provider === 'Google' ? 'dein.name@googlemail.com' : 'deine.email@beispiel.de'}
              value={socialModal.email}
              onChange={(e) => setSocialModal(prev => ({ ...prev, email: e.target.value, error: '' }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Dein Name (optional)"
              placeholder="z. B. Max Mustermann"
              value={socialModal.name}
              onChange={(e) => setSocialModal(prev => ({ ...prev, name: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={socialModal.submitting}
              sx={{
                mt: 1,
                py: 1.3,
                fontWeight: 700,
                backgroundColor: socialModal.provider === 'Google' ? '#1a73e8' : undefined,
                '&:hover': {
                  backgroundColor: socialModal.provider === 'Google' ? '#1557b0' : undefined,
                }
              }}
            >
              {socialModal.submitting ? <CircularProgress size={24} color="inherit" /> : `Weiter mit ${socialModal.provider}`}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setSocialModal(prev => ({ ...prev, open: false }))} 
            color="inherit"
            disabled={socialModal.submitting}
          >
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default LoginForm;
